// src/utils/enhancedLinkGenerators.js
import { 
  getCategorySlug, 
  getSubcategorySlug, 
  isValidCategory, 
  isValidSubcategory,
  createUrlSlug
} from './dynamicUrlMapping';

/**
 * Static category mapping for immediate use while dynamic system loads
 */
const staticCategoryMapping = {
  'Home Decor': 'home-decor',
  'Beauty': 'beauty',
  'Beauty & Personal Care': 'beauty-and-personal-care',
  'Grocery & Gourmet Foods': 'grocery-and-gourmet-foods',
  'Bath & Body': 'bath-and-body',
  'Beverages': 'beverages',
  'Baby & Kids': 'baby-and-kids',
  'Health & Wellness': 'health-and-wellness',
  'Clothing': 'clothing',
  'Books & Media': 'books-and-media'
};

/**
 * Enhanced link generators that work with dynamic URL mapping
 * These functions automatically handle new categories from the backend
 */

/**
 * Generate clean category URL with automatic slug generation
 * @param {string} countryCode - Country code (canada, usa, etc.)
 * @param {string} category - Category name from backend
 * @param {string} subcategory - Optional subcategory name from backend
 * @param {Object} options - Additional options
 * @returns {string} Clean URL path
 */
export const generateCategoryUrl = (countryCode, category, subcategory = null, options = {}) => {
  if (!countryCode || !category) {
    console.warn('Missing required parameters for generateCategoryUrl');
    return `/country/${countryCode || 'canada'}/products`;
  }

  // Validate category exists (with fallback)
  if (!isValidCategory(category)) {
    console.warn(`Category "${category}" not found in backend data, using as-is`);
  }

  const categorySlug = getCategorySlug(category) || staticCategoryMapping[category] || createUrlSlug(category);
  
  if (!categorySlug) {
    console.warn(`Could not generate slug for category: ${category}`);
    return `/country/${countryCode}/products`;
  }
  
  let url = `/country/${countryCode}/${categorySlug}`;
  
  if (subcategory) {
    // Validate subcategory exists (with fallback)
    if (!isValidSubcategory(subcategory)) {
      console.warn(`Subcategory "${subcategory}" not found in backend data, using as-is`);
    }
    
    const subcategorySlug = getSubcategorySlug(subcategory);
    if (subcategorySlug) {
      url += `/${subcategorySlug}`;
    }
  }
  
  // Add query parameters if specified
  if (options.preserveQuery && typeof window !== 'undefined') {
    const currentParams = new URLSearchParams(window.location.search);
    const preservedParams = new URLSearchParams();
    
    // Preserve specific parameters
    const paramsToPreserve = ['sort', 'view', 'page'];
    paramsToPreserve.forEach(param => {
      if (currentParams.has(param)) {
        preservedParams.set(param, currentParams.get(param));
      }
    });
    
    if (preservedParams.toString()) {
      url += `?${preservedParams.toString()}`;
    }
  }
  
  return url;
};

/**
 * Generate multi-country category URL
 * @param {string[]} countries - Array of country codes
 * @param {string} category - Category name from backend
 * @param {string} subcategory - Optional subcategory name
 * @returns {string} Multi-country URL path
 */
export const generateMultiCountryUrl = (countries, category, subcategory = null) => {
  if (!countries || !countries.length || !category) {
    console.warn('Missing required parameters for generateMultiCountryUrl');
    return '/multi/canada/products';
  }

  const countryList = countries.filter(Boolean).join('-');
  const categorySlug = getCategorySlug(category);
  
  if (!categorySlug) {
    console.warn(`Could not generate slug for category: ${category}`);
    return `/multi/${countryList}/products`;
  }
  
  let url = `/multi/${countryList}/${categorySlug}`;
  
  if (subcategory) {
    const subcategorySlug = getSubcategorySlug(subcategory);
    if (subcategorySlug) {
      url += `/${subcategorySlug}`;
    }
  }
  
  return url;
};

/**
 * Generate product detail URL (unchanged but enhanced)
 * @param {string} countryCode - Country code
 * @param {string} productId - Product ID
 * @param {string} productName - Product name for SEO
 * @param {Object} product - Full product object (optional, for category context)
 * @returns {string} Product URL path
 */
export const generateProductUrl = (countryCode, productId, productName = null, product = null) => {
  if (!countryCode || !productId) {
    console.warn('Missing required parameters for generateProductUrl');
    return '/products';
  }

  let url = `/${countryCode}/products/${productId}`;
  
  if (productName) {
    const nameSlug = productName.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    url += `/name/${nameSlug}`;
  }
  
  return url;
};

/**
 * Generate breadcrumb URLs for category hierarchy
 * @param {string} countryCode - Country code
 * @param {string} category - Category name
 * @param {string} subcategory - Subcategory name (optional)
 * @returns {Array} Array of breadcrumb objects with URLs
 */
export const generateBreadcrumbUrls = (countryCode, category = null, subcategory = null) => {
  const breadcrumbs = [
    {
      label: 'Home',
      url: `/${countryCode}`,
      isActive: false
    }
  ];
  
  if (category) {
    breadcrumbs.push({
      label: category,
      url: generateCategoryUrl(countryCode, category),
      isActive: !subcategory
    });
    
    if (subcategory) {
      breadcrumbs.push({
        label: subcategory,
        url: generateCategoryUrl(countryCode, category, subcategory),
        isActive: true
      });
    }
  } else {
    breadcrumbs.push({
      label: 'Products',
      url: `/${countryCode}/products`,
      isActive: true
    });
  }
  
  return breadcrumbs;
};

/**
 * Generate navigation menu URLs for all categories
 * @param {string} countryCode - Country code
 * @returns {Array} Array of navigation items with URLs
 */
export const generateNavigationUrls = (countryCode) => {
  // Import here to avoid circular dependencies
  const { urlMappingCache } = require('./dynamicUrlMapping');
  
  const categories = urlMappingCache.getAllCategories();
  
  return categories.map(category => ({
    label: category,
    url: generateCategoryUrl(countryCode, category),
    slug: getCategorySlug(category)
  }));
};

/**
 * Generate sitemap URLs for all categories and subcategories
 * @param {string} countryCode - Country code
 * @returns {Array} Array of URLs for sitemap generation
 */
export const generateSitemapUrls = (countryCode) => {
  const { urlMappingCache } = require('./dynamicUrlMapping');
  
  const categories = urlMappingCache.getAllCategories();
  const subcategories = urlMappingCache.getAllSubcategories();
  const urls = [];
  
  // Add category URLs
  categories.forEach(category => {
    urls.push({
      url: generateCategoryUrl(countryCode, category),
      priority: 0.8,
      changefreq: 'weekly'
    });
  });
  
  // Add subcategory URLs (you'd need to know which subcategories belong to which categories)
  // This would require additional backend data about category-subcategory relationships
  
  return urls;
};

/**
 * Validate and fix potentially broken URLs
 * @param {string} url - URL to validate
 * @param {string} fallbackCountry - Fallback country code
 * @returns {string} Valid URL or fallback
 */
export const validateAndFixUrl = (url, fallbackCountry = 'canada') => {
  if (!url || typeof url !== 'string') {
    return `/country/${fallbackCountry}/products`;
  }
  
  // Parse URL segments
  const segments = url.replace(/^\/+/, '').split('/').filter(Boolean);
  
  if (segments.length === 0) {
    return `/country/${fallbackCountry}`;
  }
  
  const [countryCode, categorySlug, subcategorySlug] = segments;
  
  // If this looks like a category URL, validate it
  if (categorySlug && categorySlug !== 'products' && categorySlug !== 'about' && categorySlug !== 'contact') {
    const { getCategoryFromSlug, getSubcategoryFromSlug } = require('./dynamicUrlMapping');
    
    const category = getCategoryFromSlug(categorySlug);
    if (!category) {
      console.warn(`Invalid category slug: ${categorySlug}, redirecting to products`);
      return `/country/${countryCode || fallbackCountry}/products`;
    }
    
    if (subcategorySlug) {
      const subcategory = getSubcategoryFromSlug(subcategorySlug);
      if (!subcategory) {
        console.warn(`Invalid subcategory slug: ${subcategorySlug}, redirecting to category`);
        return generateCategoryUrl(countryCode || fallbackCountry, category);
      }
    }
  }
  
  return url;
};

/**
 * Generate URL with automatic fallbacks and error handling
 * This is the main function components should use
 * @param {Object} params - URL generation parameters
 * @returns {string} Generated URL with fallbacks
 */
export const generateUrl = ({
  type = 'category',
  countryCode = 'canada',
  category = null,
  subcategory = null,
  countries = [],
  productId = null,
  productName = null,
  options = {}
}) => {
  try {
    switch (type) {
      case 'category':
        return generateCategoryUrl(countryCode, category, subcategory, options);
      
      case 'multi-country':
        return generateMultiCountryUrl(countries, category, subcategory);
      
      case 'product':
        return generateProductUrl(countryCode, productId, productName);
      
      case 'home':
        return `/country/${countryCode}`;
      
      case 'products':
        return `/country/${countryCode}/products`;
      
      default:
        console.warn(`Unknown URL type: ${type}`);
        return `/country/${countryCode}/products`;
    }
  } catch (error) {
    console.error('Error generating URL:', error);
    return `/country/${countryCode || 'canada'}/products`;
  }
};
